import { useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

interface AudioCue {
  trackId: string;
  mode: 'narration' | 'background';
  text?: string;
}

/**
 * Map of narration track id → bundled audio asset URL. Explicit mappings win;
 * otherwise the component probes /audio/mafia/{trackId}.wav, .m4a and .mp3, then
 * falls back to speechSynthesis (TTS).
 */
const AUDIO_ASSETS: Record<string, string> = {};
// Result cue ids for optional recorded files:
// roles_revealed, game_starting, night_result_killed, night_result_none, day_elimination_killed, day_elimination_none.
const DEFAULT_AUDIO_BASE = '/audio/mafia';
const DEFAULT_AUDIO_EXTENSIONS = ['wav', 'm4a', 'mp3'] as const;
const assetProbeCache = new Map<string, Promise<string | null>>();

interface AudioManagerProps {
  audio: AudioCue | null;
  enabled: boolean;
}

function pickRussianVoice(): SpeechSynthesisVoice | null {
  if (typeof window === 'undefined' || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  return (
    voices.find((v) => v.lang?.toLowerCase().startsWith('ru')) ??
    voices.find((v) => v.lang?.toLowerCase().includes('ru')) ??
    null
  );
}

export function AudioManager({ audio, enabled }: AudioManagerProps) {
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const currentTrackRef = useRef<string | null>(null);
  const unlockedRef = useRef(false);
  const speechPrimedRef = useRef(false);
  const latestAudioRef = useRef<AudioCue | null>(null);
  const enabledRef = useRef(enabled);
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const unlock = () => {
      const wasUnlocked = unlockedRef.current;
      unlockedRef.current = true;
      setUnlocked(true);
      void primeAudioElement(audioElRef);
      primeSpeechSynthesis(speechPrimedRef);
      if (!wasUnlocked && latestAudioRef.current && enabledRef.current) {
        void playCue(latestAudioRef.current, audioElRef, () => false);
      }
    };
    window.addEventListener('pointerdown', unlock, true);
    window.addEventListener('keydown', unlock, true);
    return () => {
      window.removeEventListener('pointerdown', unlock, true);
      window.removeEventListener('keydown', unlock, true);
    };
  }, []);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    latestAudioRef.current = audio;

    // Stop everything first.
    const el = audioElRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
    if (window.speechSynthesis) window.speechSynthesis.cancel();

    if (!enabled) return;
    if (!audio) {
      currentTrackRef.current = null;
      return;
    }
    currentTrackRef.current = audio.trackId;

    let cancelled = false;
    void playCue(audio, audioElRef, () => cancelled);

    return () => {
      cancelled = true;
      if (audioElRef.current) audioElRef.current.onerror = null;
    };
  }, [audio, enabled]);

  if (!enabled) return null;
  if (unlocked) return null;
  return (
    <button
      type="button"
      onClick={() => {
        unlockedRef.current = true;
        setUnlocked(true);
        void primeAudioElement(audioElRef);
        primeSpeechSynthesis(speechPrimedRef);
        if (latestAudioRef.current) void playCue(latestAudioRef.current, audioElRef, () => false);
      }}
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 300,
        border: '1px solid var(--line-strong)',
        borderRadius: 'var(--radius-btn)',
        background: 'var(--accent)',
        color: '#04140a',
        padding: '10px 14px',
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 12,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        cursor: 'pointer',
      }}
    >
      Включить звук
    </button>
  );
}

function audioAssetCandidates(trackId: string): string[] {
  const explicit = AUDIO_ASSETS[trackId];
  if (explicit) return [explicit];
  return DEFAULT_AUDIO_EXTENSIONS.map((ext) => `${DEFAULT_AUDIO_BASE}/${trackId}.${ext}`);
}

async function playCue(
  audio: AudioCue,
  audioElRef: MutableRefObject<HTMLAudioElement | null>,
  isCancelled: () => boolean,
): Promise<void> {
  const assetUrl = await resolveAudioAssetUrl(audio.trackId);

  if (isCancelled()) return;
  if (!assetUrl) {
    speakTts(audio);
    return;
  }

  const audioEl = audioElRef.current ?? new Audio();
  audioElRef.current = audioEl;
  let fellBack = false;
  const fallback = () => {
    if (fellBack || isCancelled()) return;
    fellBack = true;
    speakTts(audio);
  };
  audioEl.onerror = fallback;
  audioEl.src = assetUrl;
  audioEl.loop = audio.mode === 'background';
  audioEl.play().catch(fallback);
}

function resolveAudioAssetUrl(trackId: string): Promise<string | null> {
  const cached = assetProbeCache.get(trackId);
  if (cached) return cached;

  const probe = probeAudioAssetCandidates(audioAssetCandidates(trackId))
    .catch(() => null);
  assetProbeCache.set(trackId, probe);
  return probe;
}

async function primeAudioElement(audioElRef: MutableRefObject<HTMLAudioElement | null>): Promise<void> {
  if (typeof window === 'undefined') return;
  const audioEl = audioElRef.current ?? new Audio();
  audioElRef.current = audioEl;
  const previousSrc = audioEl.currentSrc || audioEl.src;
  const previousMuted = audioEl.muted;

  audioEl.muted = true;
  audioEl.src = `${DEFAULT_AUDIO_BASE}/roles_revealed.wav`;
  try {
    await audioEl.play();
    audioEl.pause();
    audioEl.currentTime = 0;
  } catch {
    // Browser may still require a more explicit click; the visible button keeps
    // the user-facing way to retry.
  } finally {
    audioEl.muted = previousMuted;
    if (previousSrc) audioEl.src = previousSrc;
  }
}

async function probeAudioAssetCandidates(candidates: string[]): Promise<string | null> {
  for (const assetUrl of candidates) {
    const res = await fetch(assetUrl, { method: 'HEAD', cache: 'force-cache' });
    const contentType = res.headers.get('content-type')?.toLowerCase() ?? '';
    if (res.ok && !contentType.includes('text/html') && !contentType.includes('application/json')) return assetUrl;
  }
  return null;
}

function primeSpeechSynthesis(primedRef: MutableRefObject<boolean>): void {
  if (typeof window === 'undefined' || !window.speechSynthesis || primedRef.current) return;
  const synth = window.speechSynthesis;
  primedRef.current = true;
  synth.resume();
  const utter = new SpeechSynthesisUtterance('.');
  utter.lang = 'ru-RU';
  utter.volume = 0;
  utter.rate = 10;
  synth.speak(utter);
  window.setTimeout(() => synth.resume(), 0);
}

function speakTts(audio: AudioCue): void {
  if (typeof window === 'undefined' || !window.speechSynthesis || !audio.text) return;
  const synth = window.speechSynthesis;
  synth.cancel();
  synth.resume();
  const utter = new SpeechSynthesisUtterance(audio.text);
  utter.lang = 'ru-RU';
  const voice = pickRussianVoice();
  if (voice) utter.voice = voice;
  utter.rate = 0.98;
  utter.pitch = 1;
  synth.speak(utter);
  window.setTimeout(() => synth.resume(), 0);
}
